import { useMemo, useState } from "react";
import useSWR from "swr";
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import {
  fetchMarketFlyerRowsByProductId,
  mapMarketFlyerRowsToDisplayItems,
  resolvePriceProductByCandidateNames,
  summarizeMarketFlyerRows,
} from "../../services/beefAiPricing";
import { cadCurrencyFormat } from "../pricing-reports/constants";
import MarketFlyerImageViewer from "./MarketFlyerImageViewer";

function normalizeCandidateName(value) {
  return String(value || "").trim();
}

function parseNamesFromParenthetical(rawName) {
  const source = normalizeCandidateName(rawName);
  if (!source) return [];
  const names = [source];
  const match = source.match(/^(.*?)\s*\((.*?)\)\s*$/);
  if (match) {
    names.push(normalizeCandidateName(match[1]));
    names.push(normalizeCandidateName(match[2]));
  }
  return names.filter(Boolean);
}

function buildBeefCutNameCandidates(beefCut) {
  const candidates = new Set();
  const add = (value) => {
    const normalized = normalizeCandidateName(value);
    if (normalized) candidates.add(normalized);
  };

  add(beefCut?.name);

  for (const translation of beefCut?.translations || []) {
    add(translation?.name);
  }

  for (const override of beefCut?.beef_product_overrides || []) {
    add(override?.name);
    for (const translation of override?.translations || []) {
      add(translation?.name);
    }
  }

  for (const value of [...candidates]) {
    parseNamesFromParenthetical(value).forEach(add);
  }

  return [...candidates];
}

function formatUnitPrice(item) {
  if (item?.unitPriceValue == null) return "—";
  const formatted = cadCurrencyFormat.format(item.unitPriceValue);
  return item?.pricingUnit && item.pricingUnit !== "—"
    ? `${formatted} per ${item.pricingUnit}`
    : formatted;
}

function appendQueryParams(baseUrl, params) {
  const separator = baseUrl.includes("?") ? "&" : "?";
  return `${baseUrl}${separator}${params.toString()}`;
}

function buildFlyerCardImageUrl(rawUrl) {
  const source = String(rawUrl || "").trim();
  if (!source) return "";
  const isDirectusAsset = /\/assets\//.test(source);
  if (!isDirectusAsset) return source;

  // Directus image transformations: smaller gallery payload and predictable card crop.
  const params = new URLSearchParams();
  params.set("width", "640");
  params.set("height", "640");
  params.set("fit", "cover");
  params.set("quality", "82");
  params.set("withoutEnlargement", "true");
  params.set("format", "webp");
  return appendQueryParams(source, params);
}

function SummaryMetricCard({ label, value }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase" }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ mt: 1 }}>
        {value}
      </Typography>
    </Paper>
  );
}

function MarketFlyerPreviewDialog({ open, onClose, item }) {
  const imageUrl = item?.images?.[0]?.url || item?.imageUrl || "";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        {item?.product || "Promotion"} preview
      </DialogTitle>
      <DialogContent dividers>
        {!item ? (
          <Alert severity="info">No promotion selected.</Alert>
        ) : !imageUrl ? (
          <Alert severity="info">No flyer image was attached to this promotion row.</Alert>
        ) : (
          <Stack spacing={2}>
            <MarketFlyerImageViewer
              src={imageUrl}
              alt={item?.detectionLabel || item?.product || "Market flyer"}
              pixelBox={item?.pixelBox}
              maxHeight={560}
              viewerBackground="#000"
            />
            <Divider />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2"><strong>Date:</strong> {item?.advertisedDate || "—"}</Typography>
                <Typography variant="body2"><strong>Retailer:</strong> {item?.retailer || "—"}</Typography>
                <Typography variant="body2"><strong>Province:</strong> {item?.province || "—"}</Typography>
                <Typography variant="body2"><strong>Unit price:</strong> {formatUnitPrice(item)}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2"><strong>Provider:</strong> {item?.provider || "—"}</Typography>
                <Typography variant="body2"><strong>Product:</strong> {item?.product || "—"}</Typography>
              </Grid>
            </Grid>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PromotionFlyerCard({ item, onOpen }) {
  const previewUrl = buildFlyerCardImageUrl(item?.imageUrl);

  return (
    <Card
      sx={{
        height: "100%",
        boxShadow: "0 2px 4px rgba(0,0,0,0.5)",
      }}
    >
      <CardActionArea
        onClick={onOpen}
        disabled={!item?.imageUrl}
        sx={{ height: "100%", alignItems: "stretch" }}
      >
        {previewUrl ? (
          <CardMedia
            component="img"
            image={previewUrl}
            alt={item?.detectionLabel || item?.product || "Promotion flyer"}
            sx={{
              height: 220,
              objectFit: "cover",
              backgroundColor: "#f5f5f5",
            }}
          />
        ) : (
          <Box
            sx={{
              height: 220,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#f5f5f5",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No flyer image
            </Typography>
          </Box>
        )}

        <CardContent sx={{ pt: 1.5 }}>
          <Typography
            sx={{
              fontWeight: "bold",
              color: "#090909",
              fontSize: "0.95rem",
              lineHeight: 1.3,
              mb: 0.5,
            }}
          >
            Retailer: {item?.retailer || "—"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Region: {item?.province || "—"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            Date: {item?.advertisedDate || "—"}
          </Typography>
          <Typography
            variant="caption"
            sx={{ mt: 1, display: "block", color: item?.imageUrl ? "primary.main" : "text.disabled" }}
          >
            {item?.imageUrl ? "Click to open full flyer" : "No image available"}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

function PromotionsTab({ beefCut }) {
  const [selectedRow, setSelectedRow] = useState(null);
  const candidateNames = useMemo(() => buildBeefCutNameCandidates(beefCut), [beefCut]);

  const {
    data: resolvedPriceProduct,
    error: resolveProductError,
    isLoading: isResolvingProduct,
  } = useSWR(
    candidateNames.length ? ["beef-cut-price-product", beefCut?.id, candidateNames.join("|")] : null,
    () => resolvePriceProductByCandidateNames(candidateNames),
    { revalidateOnFocus: false, dedupingInterval: 60 * 60 * 1000 }
  );

  const {
    data: marketFlyerRows = [],
    error: marketFlyerError,
    isLoading: isMarketFlyerLoading,
  } = useSWR(
    resolvedPriceProduct?.id ? ["market-flyer-by-product-id", resolvedPriceProduct.id] : null,
    () => fetchMarketFlyerRowsByProductId(resolvedPriceProduct.id),
    { revalidateOnFocus: false }
  );

  const summary = useMemo(() => summarizeMarketFlyerRows(marketFlyerRows), [marketFlyerRows]);
  const rows = useMemo(() => mapMarketFlyerRowsToDisplayItems(marketFlyerRows), [marketFlyerRows]);

  if (resolveProductError) {
    return (
      <Alert severity="error">
        {resolveProductError.message || "Unable to resolve this beef cut to pricing products."}
      </Alert>
    );
  }

  if (isResolvingProduct) {
    return (
      <Stack alignItems="center" py={3}>
        <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Resolving product mapping for promotions...
        </Typography>
      </Stack>
    );
  }

  if (!resolvedPriceProduct?.id) {
    return (
      <Alert severity="info">
        No linked promotions for this cut. The beef cut could not be strictly matched to a pricing product.
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      <Box>
        <Typography className="form-section-title" variant="h4">
          Promotions
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Linked pricing product: {resolvedPriceProduct.name}
        </Typography>
      </Box>

      {marketFlyerError ? (
        <Alert severity="error">
          {marketFlyerError.message || "Something went wrong while loading market flyer promotions."}
        </Alert>
      ) : null}

      {isMarketFlyerLoading ? (
        <Stack alignItems="center" py={3}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Loading promotions...
          </Typography>
        </Stack>
      ) : rows.length === 0 ? (
        <Alert severity="info">No promotions found for this linked product.</Alert>
      ) : (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <SummaryMetricCard label="Promotions" value={summary.totalPromotions} />
            </Grid>
            <Grid item xs={12} md={4}>
              <SummaryMetricCard label="Regions/Provinces" value={summary.distinctProvinceCount} />
            </Grid>
            <Grid item xs={12} md={4}>
              <SummaryMetricCard label="Retailers" value={summary.distinctRetailerCount} />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Promoted Regions
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
                  {summary.provinces.map((item) => (
                    <Chip key={item.province} label={`${item.province} (${item.count})`} />
                  ))}
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Retailers
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
                  {summary.retailers.map((item) => (
                    <Chip key={item.retailer} label={`${item.retailer} (${item.count})`} />
                  ))}
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Flyer Gallery
            </Typography>
            <Grid container spacing={2}>
              {rows.map((item) => (
                <Grid key={item.id} item xs={12} sm={6} md={4} lg={3}>
                  <PromotionFlyerCard item={item} onOpen={() => setSelectedRow(item)} />
                </Grid>
              ))}
            </Grid>
          </Paper>
        </>
      )}

      <MarketFlyerPreviewDialog
        open={Boolean(selectedRow)}
        onClose={() => setSelectedRow(null)}
        item={selectedRow}
      />
    </Stack>
  );
}

export default PromotionsTab;
